import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StyledHtmlContent from './common/StyledHtmlContent';
import {
  FaHome, FaDice, FaFootballBall, FaChess, FaDragon,
  FaTicketAlt, FaGift, FaUsers, FaNewspaper, FaBook,
  FaBars, FaTimes
} from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';

interface NavLink {
  label: string;
  href: string;
  icon: React.ReactNode;
}

const Navbar: React.FC = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const links: NavLink[] = [
    { label: t('navItems.home'), href: '/', icon: <FaHome className="text-inherit" /> },
    { label: t('navItems.lottery'), href: '/lottery', icon: <FaTicketAlt className="text-inherit" /> },
    { label: t('navItems.cockfighting'), href: '/cockfighting', icon: <FaDragon className="text-inherit" /> },
    { label: t('navItems.sports'), href: '/sports', icon: <FaFootballBall className="text-inherit" /> },
    { label: t('navItems.casino'), href: '/casino', icon: <FaDice className="text-inherit" /> },
    { label: t('navItems.promotions'), href: '/promotion', icon: <FaGift className="text-inherit" /> },
    { label: t('navItems.about'), href: '/about', icon: <FaUsers className="text-inherit" /> },
  ];

  return (
    <header className="bg-dark text-white fixed top-0 w-full z-40">
      <div className="container mx-auto flex items-center justify-between px-4 sm:px-6 py-4 sm:py-6">
        <img src="/logo-u888.png" alt="U888" className="h-[2rem] sm:h-[5rem]" />
        
        {/* Mobile menu button */}
        <button 
          className="lg:hidden text-2xl flex items-center justify-center w-10 h-10"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <FaBars />
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex space-x-6 items-center">
          {links.map((l) => {
            const isActive = location.pathname === l.href;
            const activeColor = isActive ? 'text-orange-500' : 'text-white';

            return (
              <Link
                key={l.label}
                to={l.href}
                className="group flex flex-col items-center space-y-1 text-base font-semibold transition-all duration-200"
              >
                <div className="group flex flex-col items-center transition-all duration-200">
                  <span className={`text-4xl sm:text-5xl transition duration-200 group-hover:text-orange-500 ${activeColor}`}>
                    {l.icon}
                  </span>
                  <span className={`text-sm sm:text-base transition duration-200 group-hover:text-orange-500 ${activeColor}`}>
                    {l.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Language Toggle */}
        <div className="flex items-center space-x-3">
          <button onClick={() => i18n.changeLanguage('vi')} className="text-sm">VI</button>
          <button onClick={() => i18n.changeLanguage('en')} className="text-sm">EN</button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`lg:hidden fixed inset-0 bg-dark z-50 transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Close button */}
        <button 
          className="absolute top-6 left-6 text-2xl p-2 hover:text-orange-500 transition-colors"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <FaTimes />
        </button>

        {/* Mobile menu content */}
        <div className="flex flex-col items-center justify-center h-full pt-16">
          <div className="grid grid-cols-1 gap-8 w-full max-w-md px-6">
            {links.map((l) => {
              const isActive = location.pathname === l.href;
              const activeColor = isActive ? 'text-orange-500' : 'text-white';

              return (
                <Link
                  key={l.label}
                  to={l.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="group flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <span className={`text-2xl transition duration-200 group-hover:text-orange-500 ${activeColor}`}>
                    {l.icon}
                  </span>
                  <span className={`text-lg transition duration-200 group-hover:text-orange-500 ${activeColor}`}>
                    {l.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="w-full bg-[#d14104] overflow-hidden">
        <div className="h-12 sm:h-16 flex items-center overflow-hidden group">
          <div className="animate-marquee whitespace-nowrap text-white text-sm sm:text-md font-medium px-4 flex items-center h-full group-hover:[animation-play-state:paused]">
            <StyledHtmlContent 
              html={t('home.add_popup')}
              className="inline-block"
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar; 